import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import "./DropdownSelector.css";

// assets
import dropDownLogo from "../../assets/new-images/dropdown-icon.svg";
import blueCloseIcon from "../../assets/new-images/blue-close-icon.svg";

import { useConstants } from "../../constant/constantProvider";
import useIsMobile from "../../hooks/useIsMobile";
import SearchBar from "../SearchBar/SearchBar";

/**
 * DropdownSelector
 *
 * @prop {array}   items                  - Array of option objects
 * @prop {any}     selected               - Currently selected value
 * @prop {func}    onChange               - (value) => void
 * @prop {object}  placeholder            - { name: string, image?: string }
 * @prop {"selected"|"selected-cross"} type
 * @prop {object}  itemConfig             - Getter functions:
 *   {
 *     getLabel:        (item) => string,
 *     getValue:        (item) => any,
 *     getSubLabel:     (item) => string | null,
 *     getDefaultImage: (item) => string | null,
 *     getActiveImage:  (item) => string | null,
 *     getDisabled:     (item) => boolean,
 *     getSubItems:     (item) => SubItem[] | null,
 *   }
 * @prop {boolean} showTriggerIcon         - Show image in trigger (default: true)
 * @prop {boolean} showActiveIconInTrigger - When false trigger always shows default image (default: true)
 * @prop {boolean} isDisabled
 * @prop {"down"|"up"} direction
 * @prop {"default"|"blue"|"transparent"} theme
 * @prop {boolean} isIconOnly             - Hide label text in trigger
 *
 * SubItem shape: { label, value, defaultImage?, activeImage? }
 */
const DropdownSelector = ({
  items = [],
  selected,
  onChange,
  placeholder = { name: "Select an option", image: null },
  type = "selected",
  itemConfig = {},
  showTriggerIcon = true,
  showActiveIconInTrigger = true,
  isDisabled = false,
  direction = "down",
  theme = "default",
  isIconOnly = false,
  searchable = false,
}) => {
  const isMobile = useIsMobile();
  const { NO_DATA_FOUND } = useConstants();

  const {
    getLabel = (item) => item.name ?? item.label ?? "",
    getValue = (item) => item.name ?? item.id ?? item,
    getSubLabel = () => null,
    getDefaultImage = (item) => item.defaultImage ?? item.image ?? null,
    getActiveImage = null,
    getDisabled = (item) => !!item.disabled,
    getSubItems = () => null,
  } = itemConfig;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [openSubMenu, setOpenSubMenu] = useState(null);   // value of open parent item
  const [subMenuPos, setSubMenuPos] = useState(null);     // { top, left } for portal
  const dropdownRef = useRef(null);
  const itemRefs = useRef({});                      // ref per item with subItems
  const closeTimerRef = useRef(null);

  const handleClose = useCallback((e) => {
    // Prevent closing if clicking on a submenu flyout
    if (e?.target?.closest('.dropdown-submenu-flyout')) {
      return;
    }
    setIsOpen(false);
    setOpenSubMenu(null);
    setSearchQuery("");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose(event);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [handleClose]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Recompute portal position when openSubMenu changes
  useEffect(() => {
    if (openSubMenu && itemRefs.current[openSubMenu]) {
      const rect = itemRefs.current[openSubMenu].getBoundingClientRect();
      setSubMenuPos({ top: rect.top, left: rect.right + 4 });
    } else {
      setSubMenuPos(null);
    }
  }, [openSubMenu]);

  // Hover helpers with grace period so mouse can travel to flyout
  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenSubMenu(null), 120);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const openSubMenuFor = useCallback((value) => {
    cancelClose();
    setOpenSubMenu(value);
  }, [cancelClose]);

  // ── Resolve selected (searches sub-items too) ──────────────────────────────
  let selectedItem = items.find((i) => getValue(i) === selected);
  let selectedSubItem = null;

  if (!selectedItem) {
    for (const item of items) {
      const subs = getSubItems(item);
      if (Array.isArray(subs)) {
        const sub = subs.find((s) => s.value === selected);
        if (sub) { selectedItem = item; selectedSubItem = sub; break; }
      }
    }
  }

  const hasSelection = !!(selectedItem || selectedSubItem);

  const selectedLabel = selectedSubItem
    ? selectedSubItem.label
    : selectedItem ? getLabel(selectedItem) : null;

  const selectedImage = (() => {
    if (selectedSubItem) return selectedSubItem.activeImage || selectedSubItem.defaultImage || null;
    if (selectedItem) {
      if (showActiveIconInTrigger && getActiveImage) return getActiveImage(selectedItem);
      return getDefaultImage(selectedItem);
    }
    return null;
  })();

  const handleToggle = () => {
    if (isDisabled) return;
    setIsOpen((p) => {
      if (p) setSearchQuery("");
      return !p;
    });
  };

  const handleSelect = (value) => {
    if (!isDisabled) { onChange(value); setIsOpen(false); setOpenSubMenu(null); setSearchQuery(""); }
  };

  const visibleItems = searchable && searchQuery.trim()
    ? items.filter((item) => String(getLabel(item)).toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : items;

  // Sub-items for the currently open flyout
  const flyoutItem = openSubMenu ? items.find((i) => getValue(i) === openSubMenu) : null;
  const flyoutSubItems = flyoutItem ? (getSubItems(flyoutItem) || []) : [];

  return (
    <div
      className={`dropdown-selector-wrapper ${isMobile ? "is-mobile" : ""}`}
      ref={dropdownRef}
    >
      <div
        className={[
          "dropdown-selector",
          isDisabled ? "disabled" : "",
          theme === "blue" ? "blue-theme" : "",
          theme === "transparent" ? "transparent-theme" : "",
          hasSelection ? "has-selection" : "",
        ].filter(Boolean).join(" ")}
      >
        {/* ── Trigger ──────────────────────────────────────────────────── */}
        <div
          className={`select-dropdown ${isDisabled ? "disabled-cursor" : ""} ${isIconOnly ? "no-text" : ""}`}
          onClick={handleToggle}
        >
          {showTriggerIcon && (selectedImage || placeholder.image) && (
            <img
              src={selectedImage || placeholder.image}
              alt={selectedLabel || placeholder.name}
              className="selected-item-icon"
            />
          )}

          {!isIconOnly && (
            <span className="selector-text" title={selectedLabel || placeholder.name}>{selectedLabel || placeholder.name}</span>
          )}

          {hasSelection && type === "selected-cross" && (
            <button
              type="button"
              className="deselect-button"
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
            >
              <img src={blueCloseIcon} alt="Deselect" />
            </button>
          )}

          {(type !== "selected-cross" || !hasSelection) && !isIconOnly && (
            <img
              src={dropDownLogo}
              alt="Dropdown Icon"
              className={`selector-arrow ${isOpen ? (direction === "down" ? "rotate" : "rotate-up") : ""}`}
            />
          )}
        </div>

        {/* ── Menu ─────────────────────────────────────────────────────── */}
        {isOpen && !isDisabled && (
          <div
            className={`dropdown-selector-menu ${isMobile ? "is-mobile" : ""} ${direction === "up" ? "drop-up" : ""}`}
          >
            {searchable && items.length > 0 && (
              <div className="dropdown-selector-search" onClick={(e) => e.stopPropagation()}>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search..."
                />
              </div>
            )}
            {items.length === 0 ? (
              <div className="no-data-found">{NO_DATA_FOUND}</div>
            ) : visibleItems.length === 0 ? (
              <div className="no-data-found">{NO_DATA_FOUND}</div>
            ) : (
              visibleItems.map((item) => {
                const value = getValue(item);
                const label = getLabel(item);
                const subLabel = getSubLabel(item);
                const isSelected = selected === value;
                const itemDisabled = getDisabled(item);
                const defaultImg = getDefaultImage(item);
                const activeImg = getActiveImage ? getActiveImage(item) : null;
                const image = isSelected && activeImg ? activeImg : defaultImg;
                const subItems = getSubItems(item);
                const hasSubItems = Array.isArray(subItems) && subItems.length > 0;
                const isSubOpen = openSubMenu === value;

                return (
                  <div
                    key={value}
                    ref={(el) => { if (hasSubItems) itemRefs.current[value] = el; }}
                    className={`selector-item ${itemDisabled ? "disabled" : ""} ${isSelected ? "selected-active" : ""} ${isSubOpen ? "submenu-open" : ""}`}
                    onClick={() => {
                      if (itemDisabled) return;
                      if (hasSubItems) {
                        // Both mobile & desktop: toggle flyout via click
                        if (isSubOpen) {
                          setOpenSubMenu(null);
                        } else {
                          setOpenSubMenu(value);
                        }
                      } else {
                        handleSelect(value);
                      }
                    }}
                    onMouseEnter={() => hasSubItems && !isMobile && openSubMenuFor(value)}
                    onMouseLeave={() => hasSubItems && !isMobile && scheduleClose()}
                  >
                    <div className="inner-selector-item">
                      <div className="item-left">
                        {image && <img src={image} alt={label} className="item-icon" />}
                        <div className="item-labels-container">
                          <span className="item-label">{label}</span>
                          {subLabel && <span className="sub-label-name">{subLabel}</span>}
                        </div>
                      </div>

                      {hasSubItems && (
                        <div className="item-right">
                          <img
                            src={dropDownLogo}
                            alt="expand"
                            className={`submenu-arrow ${isSubOpen ? "open" : ""}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ── Submenu flyout via Portal to escape overflow clipping ─────── */}
      {openSubMenu && subMenuPos && flyoutSubItems.length > 0 &&
        createPortal(
          <div
            className="dropdown-submenu-flyout"
            style={{ position: 'fixed', top: subMenuPos.top, left: subMenuPos.left, zIndex: 9999 }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {flyoutSubItems.map((sub) => {
              const isSubSelected = selected === sub.value;
              const subImage = isSubSelected && sub.activeImage ? sub.activeImage : sub.defaultImage ?? null;
              return (
                <div
                  key={sub.value}
                  className={`selector-item ${isSubSelected ? "selected-active" : ""}`}
                  onClick={() => handleSelect(sub.value)}
                >
                  <div className="inner-selector-item">
                    <div className="item-left">
                      {subImage && <img src={subImage} alt={sub.label} className="item-icon" />}
                      <div className="item-labels-container">
                        <span className="item-label">{sub.label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>,
          document.body
        )
      }
    </div>
  );
};

export default DropdownSelector;
